import { useMutation } from '@apollo/client';
import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import * as yup from 'yup';

import BaseErrors from '@components/fields/BaseErrors';
import FormButtons from '@components/fields/FormButtons';
import Select from '@components/fields/Select';
import Form from '@components/forms/Form';
import BodyCopyItalics from '@components/typography/BodyCopyItalics';
import {
  CareProfileUpdateDocument,
  CareProfileUpdateMutation,
  CareProfileUpdateMutationVariables,
  CareProfileUseCaseEnum,
  GetJourneyWithCareProfileQuery
} from '@typing/Generated';
import { handleMutationResponse } from '@utils/formUtils';
import { optionsForUseCase } from '@utils/optionUtils';

type FormData = {
  useCase: CareProfileUseCaseEnum | null;
};

type Props = {
  autofocus: boolean;
  journey: GetJourneyWithCareProfileQuery['journey'];
  onCancel?: () => void;
  onError: () => void;
  onSuccess: () => void;
};

const CareProfileUseCaseForm = ({ journey, onCancel, onError, onSuccess }: Props) => {
  const intl = useIntl();

  const defaultValues: FormData = {
    useCase: journey.careProfile.useCase ?? null
  };

  const [baseErrors, setBaseErrors] = useState<string[]>([]);

  const [updateCareProfile] = useMutation<CareProfileUpdateMutation, CareProfileUpdateMutationVariables>(
    CareProfileUpdateDocument
  );

  const schema = yup
    .object()
    .shape({
      useCase: yup
        .string()
        .oneOf(Object.values(CareProfileUseCaseEnum))
        .required('errors.careProfile.useCase.blank')
        .nullable()
        .notOneOf([null])
        .defined()
    })
    .required();

  const form = useForm<FormData>({
    defaultValues,
    mode: 'onChange',
    resolver: yupResolver(schema)
  });

  const submitCareProfile = useCallback(async () => {
    setBaseErrors([]);
    return updateCareProfile({
      variables: {
        ...form.getValues(),
        id: journey.careProfile.id
      }
    }).then(response => {
      handleMutationResponse<FormData>({
        data: response.data?.careProfileUpdate,
        intl,
        onError,
        onSuccess: () => {
          form.reset();
          onSuccess();
        },
        setBaseErrors,
        setError: form.setError,
        txBase: 'errors.careProfile'
      });
    });
  }, [form, intl, journey.careProfile.id, onError, onSuccess, updateCareProfile]);

  const useCaseOptions = optionsForUseCase(intl);

  return (
    <Form hookForm={form} onSubmit={form.handleSubmit(submitCareProfile)}>
      <BaseErrors errors={baseErrors} />
      <BodyCopyItalics>
        <FormattedMessage id="pages.careProfile.updateUseCaseWarning" />
      </BodyCopyItalics>
      <Select
        control={form.control}
        error={form.formState.errors.useCase}
        label={intl.formatMessage({ id: 'forms.careProfileNew.useCaseLabel' })}
        name="useCase"
        options={useCaseOptions}
      />
      <FormButtons hookForm={form} onCancel={onCancel} saveKey="dictionary.save" />
    </Form>
  );
};

export default CareProfileUseCaseForm;
